"use client"
import React from "react"
import { Content, isFilled } from "@prismicio/client"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import Button from "@aura-design/system/button"
import { EmblaOptionsType } from "embla-carousel"
import { DotButton, useDotButton } from "./EmblaCarouselDotButton"
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "./EmblaCarouselArrowButtons"
import useEmblaCarousel from "embla-carousel-react"

type PropType = {
  slides: number[]
  options?: EmblaOptionsType
}

const EmblaCarousel: React.FC<PropType> = (props) => {
  const { slides, options } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi)

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  return (
    <section className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((item, index) => {
            const classNameConnect: string[] = []

            if(isFilled.select(item.color)) {
              classNameConnect.push(item.color)
              classNameConnect.push("border-0")
            }

            return (
              <div className="embla__slide mx-0.5" key={index}>
                <div
                  className="fluid zoom relative h-[70vh] overflow-hidden"
                  key={index}
                >
                  {isFilled.image(item.image) &&
                    isFilled.link(item.button_link) && (
                      <PrismicNextLink field={item.button_link}>
                        <PrismicNextImage
                          field={item.image}
                          className="object-cover"
                          fallbackAlt=""
                          imgixParams={{
                            txt: item.image?.copyright,
                          }}
                          fill
                        />
                      </PrismicNextLink>
                    )}
                  <div className="absolute left-0 top-0 h-full w-full bg-dark-blue opacity-40 " />
                  {isFilled.image(item.icon) && (
                    <div className="absolute right-1 top-1">
                      <PrismicNextImage
                        field={item.icon}
                        className="w-[70px]  object-contain"
                        fallbackAlt=""
                        imgixParams={{
                          txt: item.icon?.copyright,
                        }}
                      />
                    </div>
                  )}
                  {isFilled.keyText(item.button_label) &&
                    isFilled.link(item.button_link) && (
                      <div className="absolute bottom-0 left-0 right-0 mb-5 text-center">
                        <PrismicNextLink
                          field={item.button_link}
                          passHref
                          legacyBehavior
                        >
                          <Button
                            target={
                              item.button_link?.target ? "_blank" : "_self"
                            }
                            label={item.button_label}
                            className={classNameConnect.join(" ")}
                          />
                        </PrismicNextLink>
                      </div>
                    )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="absolute left-0 top-0 bottom-0 valign">
        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
      </div>

      <div className="absolute right-0 top-0 bottom-0 valign">
        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
      </div>
      <div className="embla__controls">
        <div className="embla__dots">
          {slides.length > 3 &&
            scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={"embla__dot".concat(
                  index === selectedIndex ? " embla__dot--selected" : "",
                )}
              />
            ))}
        </div>
      </div>
    </section>
  )
}

export default EmblaCarousel
